import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useAdmin } from './AdminContext';
import {
  ShieldCheck, AlertTriangle, Archive, Trash2, UserCheck,
  RefreshCw, ChevronRight, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const STAGES = [
  { key: 'warn', label: 'Warning', icon: AlertTriangle, color: 'amber', desc: 'Inactive 30–60 days' },
  { key: 'archive', label: 'Archived', icon: Archive, color: 'orange', desc: 'Inactive 60–90 days' },
  { key: 'delete', label: 'Pending Delete', icon: Trash2, color: 'red', desc: 'Inactive 90+ days' },
  { key: 'exempt', label: 'Exempt', icon: ShieldCheck, color: 'emerald', desc: 'Protected from retention' },
];

export default function RetentionTab({ refreshTrigger }) {
  const { adminFetch, API_URL } = useAdmin();
  const [stats, setStats] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [stageUsers, setStageUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminFetch(`${API_URL}/api/v2/retention/stats`);
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (e) {
      console.error('Error loading retention stats:', e);
    } finally {
      setLoading(false);
    }
  }, [adminFetch, API_URL]);

  const loadStageUsers = useCallback(async (stage) => {
    try {
      setLoadingUsers(true);
      const res = await adminFetch(`${API_URL}/api/v2/retention/users?stage=${stage}`);
      if (res.ok) {
        const data = await res.json();
        setStageUsers(data.users || []);
      }
    } catch (e) {
      console.error('Error loading stage users:', e);
    } finally {
      setLoadingUsers(false);
    }
  }, [adminFetch, API_URL]);

  useEffect(() => { loadStats(); }, [loadStats, refreshTrigger]);

  useEffect(() => {
    if (selectedStage) loadStageUsers(selectedStage);
  }, [selectedStage, loadStageUsers]);

  const handleExempt = async (userId) => {
    try {
      const res = await adminFetch(`${API_URL}/api/v2/retention/exempt/${userId}`, { method: 'POST' });
      if (res.ok) {
        toast.success('User exempted from retention');
        loadStageUsers(selectedStage);
        loadStats();
      } else {
        toast.error('Failed to exempt user');
      }
    } catch { toast.error('Error exempting user'); }
  };

  const handleRemoveExemption = async (userId) => {
    try {
      const res = await adminFetch(`${API_URL}/api/v2/retention/exempt/${userId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Exemption removed');
        loadStageUsers(selectedStage);
        loadStats();
      } else {
        toast.error('Failed to remove exemption');
      }
    } catch { toast.error('Error removing exemption'); }
  };

  const daysAgo = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="retention-tab">
      {/* Policy summary */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
            Data Retention Policy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="text-slate-300">Warning email at <strong className="text-white">{stats?.thresholds?.warn_days || 30}</strong> days</span>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-600 hidden sm:block self-center" />
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-orange-400" />
              <span className="text-slate-300">Archive at <strong className="text-white">{stats?.thresholds?.archive_days || 60}</strong> days</span>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-600 hidden sm:block self-center" />
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-400" />
              <span className="text-slate-300">Permanent delete at <strong className="text-white">{stats?.thresholds?.delete_days || 90}</strong> days</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stage cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STAGES.map(s => {
          const count = stats?.[s.key === 'warn' ? 'warn_stage' : s.key === 'archive' ? 'archive_stage' : s.key === 'delete' ? 'delete_stage' : 'exempt'] || 0;
          const Icon = s.icon;
          const isSelected = selectedStage === s.key;
          return (
            <Card
              key={s.key}
              onClick={() => setSelectedStage(isSelected ? null : s.key)}
              className={`cursor-pointer transition-all border ${
                isSelected
                  ? `bg-${s.color}-500/20 border-${s.color}-500/50`
                  : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
              }`}
              data-testid={`retention-stage-${s.key}`}
            >
              <CardContent className="p-4 text-center">
                <Icon className={`h-8 w-8 mx-auto mb-2 text-${s.color}-400`} />
                <div className="text-2xl font-bold text-white">{count}</div>
                <div className="text-sm text-slate-400">{s.label}</div>
                <div className="text-xs text-slate-500 mt-1">{s.desc}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* User list for selected stage */}
      {selectedStage && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center justify-between">
              <span>{STAGES.find(s => s.key === selectedStage)?.label} Users</span>
              <span className="text-sm font-normal text-slate-400">{stageUsers.length} users</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingUsers ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : stageUsers.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No users in this stage.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {stageUsers.map(u => (
                  <div key={u.user_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 border border-slate-700"
                    data-testid={`retention-user-${u.user_id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-white truncate">{u.name || 'Unknown'}</div>
                      <div className="text-xs text-slate-400 truncate">{u.email}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <Clock className="h-3 w-3" />
                        Last login: {u.last_login ? daysAgo(u.last_login) : 'Never'}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0 ml-3">
                      {selectedStage !== 'exempt' ? (
                        <Button size="sm" variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleExempt(u.user_id); }}
                          className="border-emerald-600 text-emerald-400 hover:bg-emerald-500/20 text-xs"
                          data-testid={`exempt-user-${u.user_id}`}>
                          <UserCheck className="h-3 w-3 mr-1" /> Exempt
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleRemoveExemption(u.user_id); }}
                          className="border-red-600 text-red-400 hover:bg-red-500/20 text-xs"
                          data-testid={`unexempt-user-${u.user_id}`}>
                          <Trash2 className="h-3 w-3 mr-1" /> Remove
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
